import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, query, where, addDoc, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom"; // ✅ Added useLocation for debugging
import { ProgressBar } from "react-bootstrap";
import OnboardingMultipleChoiceQuestion from "./OnboardingMultipleChoiceQuestion";
import OnboardingMultipleChoiceQuestionChart from "./OnboardingMultipleChoiceQuestionChart";
import OnboardingTrueFalseQuestion from "./OnboardingTrueFalseQuestion";
import OnboardingTrueFalseQuestionChart from "./OnboardingTrueFalseQuestionChart";
import OnboardingMatchTheFollowingQuestion from "./OnboardingMatchTheFollowingQuestion";

const MAX_INCORRECT = 20;

const OnboardingSkillTest = () => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation(); // ✅ Get current route
  const auth = getAuth();
  const db = getFirestore();

  console.log("🚀 OnboardingSkillTest Mounted on Path:", location.pathname);

  // ✅ Ensure correct route is being hit
  useEffect(() => {
    console.log("📍 Current Route:", location.pathname);
  }, [location.pathname]);

  // ✅ Set dark background on mount
  useEffect(() => {
    document.body.style.backgroundColor = "#000";
    document.body.style.color = "#fff";

    return () => {
      console.log("🛑 OnboardingSkillTest Unmounted!");
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    };
  }, []);

  // ✅ Fetch onboarding skill test questions (lessonid = "1.0")
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        console.log("📡 Fetching questions for lessonid: 1.0...");

        const q = query(
          collection(db, "questions_new"),
          where("lessonid", "==", "1.0"), // ✅ Ensure Firestore has lessonid as "1.0" (string)
          where("questiontypeid", "in", ["1", "2", "3", "6.1", "6.2"]),
          where("active", "==", "0")
        );

        const snapshot = await getDocs(q);
        const lessonQuestions = snapshot.docs.map((doc) => doc.data());

        console.log("✅ Fetched questions count:", lessonQuestions.length);

        if (lessonQuestions.length === 0) {
          console.error("❌ No onboarding questions found for lessonid: 1.0");
          alert("No onboarding questions found. Redirecting to error page...");
          navigate("/error"); // ✅ Redirect to error page instead of course-landing
          return;
        }

        setQuestions(lessonQuestions);
        console.log("✅ Questions:", lessonQuestions);
      } catch (error) {
        console.error("❌ Error fetching onboarding questions:", error);
        alert("Error loading questions. Redirecting...");
        navigate("/error"); // ✅ Redirect to error page if fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [db, navigate]);

  // ✅ Track incorrect answers
  useEffect(() => {
    let incorrectAnswers = parseInt(localStorage.getItem("incorrectAnswerCount")) || 0;
    let lastAttemptTimestamp = parseInt(localStorage.getItem("lastAttemptTimestamp")) || 0;
    let currentTimestamp = Date.now();

    if (lastAttemptTimestamp && currentTimestamp - lastAttemptTimestamp > 24 * 60 * 60 * 1000) {
      localStorage.setItem("incorrectAnswerCount", "0");
      incorrectAnswers = 0;
    }

    console.log("📊 Incorrect Answer Count:", incorrectAnswers);
    setIncorrectCount(incorrectAnswers);
  }, []);

  // ✅ Handle answer submission
  const handleAnswerResult = async (isAnswerCorrect) => {
    console.log("📝 Answer Submitted - Correct:", isAnswerCorrect);

    if (!isAnswerCorrect) {
      let incorrectAnswers = incorrectCount + 1;
      localStorage.setItem("incorrectAnswerCount", incorrectAnswers);
      localStorage.setItem("lastAttemptTimestamp", Date.now().toString());
      setIncorrectCount(incorrectAnswers);
    }
    handleNext();
  };

  // ✅ Move to next question or finish
  const handleNext = async () => {
    console.log("👉 Moving to next question. Current index:", currentIndex);

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      console.log("✅ Finished onboarding test, redirecting to post-activation-quote");
      await saveUserPracticeSession();
      navigate("/post-activation-quote");
    }
  };

  // ✅ Save practice session after finishing the test
  const saveUserPracticeSession = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const sessionData = {
          challengeid: "0.0",
          date: Timestamp.now(),
          lessonid: "1.0",
          userid: user.uid,
        };

        await addDoc(collection(db, "userpracticesessions"), sessionData);
        console.log("✅ User practice session recorded:", sessionData);
      } else {
        console.error("❌ No authenticated user found.");
      }
    } catch (error) {
      console.error("❌ Error inserting user practice session:", error);
    }
  };

  // ✅ Prevent rendering until data is fetched
  if (loading) {
    return <p style={{ color: "#fff", textAlign: "center" }}>Loading...</p>;
  }

  return (
    <>
      {/* ✅ Progress Bar */}
      <div style={{
        width: "100vw",
        backgroundColor: "#000",
        padding: "10px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "50%",
        }}>
          <ProgressBar
            now={((currentIndex + 1) / questions.length) * 100}
            variant="success"
            style={{
              width: "40%",
              height: "6px",
              borderRadius: "5px",
              backgroundColor: "#000",
            }}
          />
        </div>
      </div>

      {/* ✅ Question Display */}
      <div style={{
        fontFamily: "Nunito Sans",
        minHeight: "100vh",
        padding: "20px",
        position: "relative",
        backgroundColor: "#000",
        color: "#fff",
      }}>
        {questions.length > 0 ? (
          <>
            {questions[currentIndex].questiontypeid === "1" && (
              <OnboardingMultipleChoiceQuestion
                question={questions[currentIndex]}
                onNext={handleAnswerResult}
              />
            )}
            {questions[currentIndex].questiontypeid === "2" && (
              <OnboardingTrueFalseQuestion
                question={questions[currentIndex]}
                onNext={handleAnswerResult}
              />
            )}
            {questions[currentIndex].questiontypeid === "3" && (
              <OnboardingMatchTheFollowingQuestion
                question={questions[currentIndex]}
                onNext={handleAnswerResult}
              />
            )}
            {questions[currentIndex].questiontypeid === "6.1" && (
              <OnboardingMultipleChoiceQuestionChart
                question={questions[currentIndex]}
                onNext={handleAnswerResult}
              />
            )}
            {questions[currentIndex].questiontypeid === "6.2" && (
              <OnboardingTrueFalseQuestionChart
                question={questions[currentIndex]}
                onNext={handleAnswerResult}
              />
            )}
          </>
        ) : (
          <p style={{ textAlign: "center", color: "#fff" }}>No questions available.</p>
        )}
      </div>
    </>
  );
};

export default OnboardingSkillTest;









// import React, { useState, useEffect } from "react";
// import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
// import { useNavigate } from "react-router-dom";
// import Logout from "../../Logout";
// import OnboardingMultipleChoiceQuestion from "./OnboardingMultipleChoiceQuestion";
// import OnboardingMultipleChoiceQuestionChart from "./OnboardingMultipleChoiceQuestionChart";
// import OnboardingTrueFalseQuestion from "./OnboardingTrueFalseQuestion";
// import OnboardingTrueFalseQuestionChart from "./OnboardingTrueFalseQuestionChart"; // ✅ Added Chart-Based True/False
// import OnboardingMatchTheFollowingQuestion from "./OnboardingMatchTheFollowingQuestion";
// import { ProgressBar } from "react-bootstrap";

// const MAX_INCORRECT = 20;

// const OnboardingSkillTest = () => {
//   const [questions, setQuestions] = useState([]);
//   const [currentIndex, setCurrentIndex] = useState(0);
//   const [incorrectCount, setIncorrectCount] = useState(0);
//   const [showProSubscription, setShowProSubscription] = useState(false);
//   const navigate = useNavigate();

//   // Set dark background on mount
//   useEffect(() => {
//     document.body.style.backgroundColor = "#000";
//     document.body.style.color = "#fff";
//     return () => {
//       document.body.style.backgroundColor = "";
//       document.body.style.color = "";
//     };
//   }, []);

//   // Load incorrect answer count from localStorage
//   useEffect(() => {
//     let incorrectAnswers = parseInt(localStorage.getItem("incorrectAnswerCount")) || 0;
//     let lastAttemptTimestamp = parseInt(localStorage.getItem("lastAttemptTimestamp")) || 0;
//     let currentTimestamp = Date.now();

//     if (lastAttemptTimestamp && currentTimestamp - lastAttemptTimestamp > 24 * 60 * 60 * 1000) {
//       localStorage.setItem("incorrectAnswerCount", "0");
//       incorrectAnswers = 0;
//     }

//     setIncorrectCount(incorrectAnswers);

//     if (incorrectAnswers >= MAX_INCORRECT) {
//       setShowProSubscription(true);
//     }
//   }, []);

//   // Fetch onboarding skill test questions from Firebase (lessonid = "1.0")
//   useEffect(() => {
//     const fetchQuestions = async () => {
//       const db = getFirestore();
//       try {
//         const q = query(
//           collection(db, "questions_new"),
//           where("lessonid", "==", "1.0"),
//           where("questiontypeid", "in", ["1", "2", "3", "6.1", "6.2"]), // ✅ Added "6.2" for chart-based True/False
//           where("active", "==", "0")
//         );
//         const snapshot = await getDocs(q);
//         const lessonQuestions = snapshot.docs.map((doc) => doc.data());
//         setQuestions(lessonQuestions);
//       } catch (error) {
//         console.error("❌ Error fetching questions:", error);
//       }
//     };

//     fetchQuestions();
//   }, []);

//   // Handle answer result
//   const handleAnswerResult = (isAnswerCorrect) => {
//     if (!isAnswerCorrect) {
//       let incorrectAnswers = incorrectCount + 1;
//       localStorage.setItem("incorrectAnswerCount", incorrectAnswers);
//       localStorage.setItem("lastAttemptTimestamp", Date.now().toString());
//       setIncorrectCount(incorrectAnswers);

//       if (incorrectAnswers >= MAX_INCORRECT && !showProSubscription) {
//         setShowProSubscription(true);
//         return;
//       }
//     }
//     handleNext();
//   };

//   // Move to next question or finish quiz
//   const handleNext = () => {
//     if (currentIndex < questions.length - 1) {
//       setCurrentIndex(currentIndex + 1);
//     } else {
//       navigate("/post-activation-quote");
//     }
//   };

//   const currentQuestion = questions[currentIndex];

//   return (
//     <>
//       {/* Progress Bar */}
//       <div
//         style={{
//           width: "100vw",
//           backgroundColor: "#000",
//           padding: "10px 0",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           position: "relative",
//         }}
//       >
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             gap: "8px",
//             width: "50%",
//           }}
//         >
//           <ProgressBar
//             now={((currentIndex + 1) / questions.length) * 100}
//             variant="success"
//             style={{
//               width: "40%",
//               height: "6px",
//               borderRadius: "5px",
//               backgroundColor: "#000",
//             }}
//           />
//         </div>
//       </div>

//       {/* Quiz Content */}
//       <div
//         style={{
//           fontFamily: "Nunito Sans",
//           minHeight: "100vh",
//           padding: "20px",
//           position: "relative",
//           backgroundColor: "#000",
//           color: "#fff",
//         }}
//       >
//         {currentQuestion ? (
//           <>
//             {currentQuestion.questiontypeid === "1" && (
//               <OnboardingMultipleChoiceQuestion
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//             {currentQuestion.questiontypeid === "2" && (
//               <OnboardingTrueFalseQuestion
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//             {currentQuestion.questiontypeid === "3" && (
//               <OnboardingMatchTheFollowingQuestion
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//             {currentQuestion.questiontypeid === "6.1" && (
//               <OnboardingMultipleChoiceQuestionChart
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//             {currentQuestion.questiontypeid === "6.2" && ( // ✅ Added chart-based True/False handling
//               <OnboardingTrueFalseQuestionChart
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//           </>
//         ) : (
//           <p>Loading questions...</p>
//         )}
//       </div>
//     </>
//   );
// };

// export default OnboardingSkillTest;